import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { cn } from '@/styles/tailwind-utils';
import { AccountLayout } from '@components/layout/AccountLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { mapChartTemplateError, useSystemChartTemplate } from '@features/chartTemplates';
import type {
  ChartTemplateField,
  ChartTemplateSection,
  ChartTemplateStatus,
  ChartTemplateVersionDefinition,
} from '@features/chartTemplates';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSpecialty(tag: string): string {
  const spaced = tag.replace(/[-_]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A section together with the fields that belong to it, in render order. */
interface FieldGroup {
  section: ChartTemplateSection | null;
  fields: ChartTemplateField[];
}

/**
 * Bucket fields under their section (ordered by `section.order`), with any
 * unsectioned fields collected into a trailing `null`-section group. Fields
 * within each group are sorted by their own `order`.
 */
function groupFields(definition: ChartTemplateVersionDefinition): FieldGroup[] {
  const byOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;
  const sections = [...definition.sections].sort(byOrder);

  const groups: FieldGroup[] = sections.map((section) => ({
    section,
    fields: definition.fields.filter((f) => f.sectionId === section.sectionId).sort(byOrder),
  }));

  const knownSectionIds = new Set(sections.map((s) => s.sectionId));
  const unsectioned = definition.fields
    .filter((f) => !f.sectionId || !knownSectionIds.has(f.sectionId))
    .sort(byOrder);

  if (unsectioned.length > 0) groups.push({ section: null, fields: unsectioned });

  return groups;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<ChartTemplateStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  DRAFT: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  ARCHIVED: 'bg-gray-100 text-gray-600 ring-gray-500/20',
};

function StatusBadge({ status, label }: { status: ChartTemplateStatus; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        STATUS_STYLES[status],
      )}
    >
      {label}
    </span>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 10L6 7l3-3" />
    </svg>
  );
}

// ─── Field preview ──────────────────────────────────────────────────────────────

const PREVIEW_INPUT =
  'pointer-events-none w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground';

/** Renders a single field as a read-only, form-like preview of how it would appear. */
function FieldPreview({ field }: { field: ChartTemplateField }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-border bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{field.label}</span>
        {field.required && (
          <span className="text-xs font-medium text-destructive">
            {t('chartTemplates.view.required')}
          </span>
        )}
        <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t(`chartTemplates.fieldTypes.${field.type}`)}
        </span>
      </div>

      {field.type === 'TEXTAREA' ? (
        <div className={cn(PREVIEW_INPUT, 'min-h-[64px]')}>{field.placeholder ?? ' '}</div>
      ) : field.type === 'DROPDOWN' ? (
        <div className="space-y-1.5">
          <div className={cn(PREVIEW_INPUT, 'flex items-center justify-between')}>
            <span>{field.placeholder ?? t('chartTemplates.view.selectPlaceholder')}</span>
            <span aria-hidden="true">▾</span>
          </div>
          {field.options && field.options.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {field.options.map((opt) => (
                <li
                  key={opt.value}
                  className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : field.type === 'CHECKBOX_GROUP' ? (
        <ul className="space-y-1.5">
          {(field.options ?? []).map((opt) => (
            <li key={opt.value} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span
                className="h-4 w-4 shrink-0 rounded border border-border bg-muted/30"
                aria-hidden="true"
              />
              {opt.label}
            </li>
          ))}
        </ul>
      ) : field.type === 'CANVAS' ? (
        <div
          className="flex items-center justify-center rounded-md border border-dashed border-border bg-muted/20 py-8 text-xs text-muted-foreground"
          style={{ aspectRatio: (field.canvasAspectRatio ?? '16:9').replace(':', ' / ') }}
        >
          {t('chartTemplates.view.canvasPreview', { ratio: field.canvasAspectRatio ?? '—' })}
        </div>
      ) : field.type === 'NUMERIC' ? (
        <div className={cn(PREVIEW_INPUT, 'flex items-center justify-between')}>
          <span>{field.placeholder ?? '0'}</span>
          {field.unit && <span className="text-xs">{field.unit}</span>}
        </div>
      ) : (
        // TEXT and DATE
        <div className={PREVIEW_INPUT}>
          {field.type === 'DATE'
            ? t('chartTemplates.view.datePlaceholder')
            : (field.placeholder ?? ' ')}
        </div>
      )}

      {/* Constraints footnote */}
      {(field.maxLength || field.min !== undefined || field.max !== undefined) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {field.maxLength && t('chartTemplates.view.maxLength', { count: field.maxLength })}
          {field.min !== undefined &&
            field.max !== undefined &&
            t('chartTemplates.view.range', { min: field.min, max: field.max })}
        </p>
      )}
    </div>
  );
}

// ─── Definition body ────────────────────────────────────────────────────────────

function TemplateDefinition({ definition }: { definition: ChartTemplateVersionDefinition }) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupFields(definition), [definition]);

  if (definition.fields.length === 0) {
    return (
      <div className="rounded-md border border-border bg-white px-5 py-12 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">{t('chartTemplates.view.noFields')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map((group, i) => (
        <section key={group.section?.sectionId ?? `unsectioned-${i}`} className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {group.section?.label ?? t('chartTemplates.view.generalSection')}
          </h2>
          <div className="space-y-3">
            {group.fields.map((field) => (
              <FieldPreview key={field.fieldId} field={field} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ChartTemplateViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { template, isLoading, fetchError } = useSystemChartTemplate(templateId);

  const definition = template?.activeVersionDefinition ?? null;

  return (
    <AccountLayout>
      {isLoading ? (
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
          <BrandLoader
            title={t('chartTemplates.loading.title')}
            subtitle={t('chartTemplates.loading.subtitle')}
          />
        </div>
      ) : fetchError ? (
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
          <div role="alert" className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            {mapChartTemplateError(fetchError, t, 'chartTemplates.errors.loadFailed')}
          </div>
        </div>
      ) : template ? (
        <>
          {/* Header */}
          <div className="border-b border-border bg-white shadow-sm">
            <div className="mx-auto max-w-4xl px-4 py-5 md:px-6">
              <Link
                to="/account/settings/chart-templates"
                className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeftIcon />
                {t('chartTemplates.view.back')}
              </Link>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <h1 className="font-display text-xl font-semibold text-foreground">
                  {template.name}
                </h1>
                <StatusBadge
                  status={template.status}
                  label={t(`chartTemplates.status.${template.status}`)}
                />
                {template.specialtyTag && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {formatSpecialty(template.specialtyTag)}
                  </span>
                )}
              </div>

              {template.description && (
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                  {template.description}
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {definition ? (
                  <span>
                    {t('chartTemplates.view.versionLabel', { version: definition.version })}
                    {' · '}
                    {t('chartTemplates.view.publishedOn', {
                      date: formatDate(definition.publishedAt),
                    })}
                  </span>
                ) : (
                  <span>{t('chartTemplates.versionNone')}</span>
                )}
                {definition?.changelogNote && (
                  <span className="italic">“{definition.changelogNote}”</span>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="mx-auto max-w-4xl px-4 py-8 md:px-6">
            {definition ? (
              <TemplateDefinition definition={definition} />
            ) : (
              <div className="rounded-md border border-border bg-white px-5 py-12 text-center shadow-sm">
                <p className="text-sm text-muted-foreground">
                  {t('chartTemplates.view.noDefinition')}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="mx-auto max-w-4xl px-4 py-16 text-center md:px-6">
          <p className="text-sm font-medium text-foreground">{t('chartTemplates.view.notFound')}</p>
          <button
            type="button"
            onClick={() => navigate('/account/settings/chart-templates')}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ChevronLeftIcon />
            {t('chartTemplates.view.back')}
          </button>
        </div>
      )}
    </AccountLayout>
  );
}
